/* eslint-disable react/style-prop-object */
import React, { ChangeEvent, useState } from "react";
import { Badge, ButtonIcon, Checkbox, Dropdown, Table } from "@optimizely/axiom";

import styles from "../../pages/UserList/UserList.module.scss";
import { User } from "../../../domain/User";
import { ATTRIBUTE_ROLES, USER_EXTERNAL_STATUS } from "../../../constants/constants";
import classnames from "classnames";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../feature-flags/flags";
import LimitByRole from "../LimitByRole/LimitByRole";
import { useFormContext } from "../UserForm/UserFormContext/UserFormContext";
import { MoreMenu } from "../MoreMenu/MoreMenu";
import { ExternalUserIndicator } from "../ExternalUserIndicator/ExternalUserIndicator";
import { ActivationStatusesModal } from "../ActivationStatusesTable/ActivationStatusesModal";

const UserTable = ({
    disableDeleteFromOptiId = false,
    onDeleteUsers,
    onRemoveAccess,
    onSelectionChange,
    onViewDetails,
    organizationId,
    selectedUsers = [],
    userList
}: {
    disableDeleteFromOptiId?: boolean;
    organizationId?: string;
    onDeleteUsers?: (users: User[]) => void;
    onRemoveAccess?: (users: User[]) => void;
    onSelectionChange?: ({ users }: { users: User[] }) => void;
    onViewDetails?: (user: User) => void;
    selectedUsers?: User[];
    userList?: User[];
}) => {
    const [showExternalStatusDefinitions, setShowExternalStatusDefinitions] = useState(false);
    const { enabled: showLoginStatus } = useFeatureFlag(Flags.SHOW_LOGIN_STATUS);
    const { enabled: showExternalUserIndicator } = useFeatureFlag(Flags.ENABLE_EXTERNAL_USER_STATUS_DISPLAY);
    const { enabled: showExternalStatusColumn } = useFeatureFlag(Flags.SHOW_EXTERNAL_USER_STATUS_COLUMN);
    const { enabled: enableBatchDeletion } = useFeatureFlag(Flags.ENABLE_BATCH_USER_DELETION);
    const { enabled: enableRebrand } = useFeatureFlag(Flags.ENABLE_REBRAND);
    const { updateAccessManagementUserState } = useFormContext();
    const openViewUser = (user: User, editMode?: boolean) => {
        updateAccessManagementUserState({ user, editing: editMode });
    };

    const handleSelectAll = (change: ChangeEvent) => {
        if (!onSelectionChange) return;
        const target = change.target as HTMLInputElement;
        const checked = target.checked;
        if (checked && selectedUsers.length !== userList?.length) {
            onSelectionChange({ users: userList || [] });
        } else if (!checked) {
            onSelectionChange({ users: [] });
        }
    };

    const handleUserSelectionChange = ({ selected, user }: { selected: boolean; user: User }) => {
        if (!onSelectionChange) return;
        if (selected) {
            onSelectionChange({ users: [user, ...selectedUsers] });
        } else {
            onSelectionChange({ users: selectedUsers.filter((u) => u.id !== user.id) });
        }
    };

    const displayRecord = (user: User) => {
        const { email, externalStatus, firstName, homeOrganizationId, id, lastName, properties = [] } = user;
        const isExternal = homeOrganizationId !== organizationId;
        const activationStatus =
            properties.find((p) => p.name?.toLowerCase() === "activateuser")?.value === "true" ? "Enabled" : "Disabled";
        const activationStatusClasses = classnames(styles["user-list__activation-status"], {
            [styles["user-list__activation-status--active"]]: activationStatus === "Enabled"
        });
        const externalStatusValue = isExternal ? "N/A" : USER_EXTERNAL_STATUS[externalStatus] || "N/A";
        const externalStatusClasses = classnames(styles["user-list__external-status"], {
            [styles["user-list__external-status--active"]]:
                [USER_EXTERNAL_STATUS.ACTIVE].indexOf(externalStatusValue) > -1,
            [styles["user-list__external-status--pending"]]:
                [
                    USER_EXTERNAL_STATUS.RECOVERY,
                    USER_EXTERNAL_STATUS.PASSWORDEXPIRED,
                    USER_EXTERNAL_STATUS.PROVISIONED
                ].indexOf(externalStatusValue) > -1,
            [styles["user-list__external-status--red"]]:
                [
                    USER_EXTERNAL_STATUS.LOCKEDOUT,
                    USER_EXTERNAL_STATUS.DEPROVISIONED,
                    USER_EXTERNAL_STATUS.SUSPENDED
                ].indexOf(externalStatusValue) > -1
        });
        const checkbox = (
            <Checkbox
                data-cy="user-table-checkbox"
                onChange={(e) =>
                    handleUserSelectionChange({
                        selected: (e.target as HTMLInputElement).checked,
                        user
                    })
                }
                checked={Boolean(id && selectedUsers.find((u) => u.id === id))}
            />
        );
        const moreMenu = (
            <MoreMenu>
                <Dropdown.ListItem>
                    <Dropdown.BlockLink
                        onClick={() => {
                            if (onViewDetails) {
                                onViewDetails(user);
                            }
                        }}
                    >
                        <span>View Details...</span>
                    </Dropdown.BlockLink>
                </Dropdown.ListItem>
                <Dropdown.ListItem>
                    <Dropdown.BlockLink
                        onClick={() => {
                            if (onRemoveAccess) {
                                onRemoveAccess([user]);
                            }
                        }}
                    >
                        <span className="danger">
                            {disableDeleteFromOptiId ? "Delete User..." : "Remove access..."}
                        </span>
                    </Dropdown.BlockLink>
                </Dropdown.ListItem>
                {!disableDeleteFromOptiId && (
                    <LimitByRole mode="hide" action={ATTRIBUTE_ROLES.USERS.UPDATE}>
                        <Dropdown.ListItem>
                            <Dropdown.BlockLink
                                onClick={() => {
                                    if (onDeleteUsers) {
                                        onDeleteUsers([user]);
                                    }
                                }}
                            >
                                <span className="danger">Delete user...</span>
                            </Dropdown.BlockLink>
                        </Dropdown.ListItem>
                    </LimitByRole>
                )}
            </MoreMenu>
        );
        return (
            <Table.TR
                key={id}
                data-cy="user-table-row"
                {...(enableRebrand ? { isLink: true, onRowClick: () => openViewUser(user) } : {})}
            >
                {enableBatchDeletion && (
                    <Table.TD>
                        {enableRebrand ? (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                            <div onClick={(e) => e.stopPropagation()}>{checkbox}</div>
                        ) : (
                            checkbox
                        )}
                    </Table.TD>
                )}
                <Table.TD>
                    {enableRebrand ? (
                        firstName
                    ) : (
                        <button className="link button-as-link" type="button" onClick={() => openViewUser(user)}>
                            {firstName}
                        </button>
                    )}
                </Table.TD>
                <Table.TD>
                    {enableRebrand ? (
                        lastName
                    ) : (
                        <button className="link button-as-link" type="button" onClick={() => openViewUser(user)}>
                            {lastName}
                        </button>
                    )}
                </Table.TD>
                <Table.TD>
                    {enableRebrand ? (
                        <span className="flex align-items-center">
                            {email}{" "}
                            {showExternalUserIndicator && isExternal && (
                                <Badge className="oui-badge--external push--left">External</Badge>
                            )}
                        </span>
                    ) : (
                        <button
                            className="link button-as-link flex align-items-center"
                            type="button"
                            onClick={() => openViewUser(user)}
                        >
                            {email}{" "}
                            {showExternalUserIndicator && isExternal && (
                                <ExternalUserIndicator className="push--left" />
                            )}
                        </button>
                    )}
                </Table.TD>
                {showExternalStatusColumn && (
                    <Table.TD className={externalStatusClasses}>{externalStatusValue}</Table.TD>
                )}
                {showLoginStatus && (
                    <Table.TD>
                        <span className={activationStatusClasses}>{activationStatus}</span>
                    </Table.TD>
                )}
                <Table.TD>
                    {user.created.toLocaleString("en-US", {
                        year: "numeric",
                        day: "numeric",
                        month: "short"
                    })}
                </Table.TD>
                <LimitByRole action={ATTRIBUTE_ROLES.USERS.READ} mode="hide">
                    <Table.TD colSpan={1} className="user-manager__table--more-menu">
                        {enableRebrand ? (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                            <div onClick={(e) => e.stopPropagation()}>{moreMenu}</div>
                        ) : (
                            moreMenu
                        )}
                    </Table.TD>
                </LimitByRole>
            </Table.TR>
        );
    };

    const tableClasses = classnames("list-table flex flex--column", styles["user-list__table"]);
    return (
        <LimitByRole action={ATTRIBUTE_ROLES.USERS.READ} mode="unauthorized">
            <div className={tableClasses} data-testid="user-table">
                {showExternalStatusDefinitions && (
                    <ActivationStatusesModal closeText="Close" showModal={setShowExternalStatusDefinitions} />
                )}
                {userList && userList.length > 0 && (
                    <>
                        <div className="user-manager__table" data-cy="user-table-scroll-container">
                            <Table className="push-double--bottom" density="loose" style="rule-no-bottom-border">
                                <Table.THead>
                                    <Table.TR>
                                        {enableBatchDeletion && (
                                            <Table.TH sorting={{}} width="50px">
                                                <Checkbox
                                                    data-cy="user-select-all"
                                                    checked={selectedUsers.length === userList.length}
                                                    indeterminate={
                                                        selectedUsers.length > 0 &&
                                                        selectedUsers.length !== userList.length
                                                    }
                                                    onChange={handleSelectAll}
                                                />
                                            </Table.TH>
                                        )}
                                        <Table.TH sorting={{}} width="25%">
                                            First Name
                                        </Table.TH>
                                        <Table.TH sorting={{}} width="25%">
                                            Last Name
                                        </Table.TH>
                                        <Table.TH sorting={{}} width={showExternalStatusColumn ? "30%" : "35%"}>
                                            Email
                                        </Table.TH>
                                        {showExternalStatusColumn && (
                                            <Table.TH sorting={{}} width="15%">
                                                <div className="flex">
                                                    Activation Status
                                                    <ButtonIcon
                                                        style="unstyled"
                                                        data-cy="external-status-column"
                                                        iconName="circle-info"
                                                        size="small"
                                                        className="display--inline"
                                                        title="Show Status Definitions"
                                                        onClick={() => setShowExternalStatusDefinitions(true)}
                                                    />
                                                </div>
                                            </Table.TH>
                                        )}
                                        {showLoginStatus && (
                                            <Table.TH sorting={{}} width="15%">
                                                Status
                                            </Table.TH>
                                        )}
                                        <Table.TH sorting={{}} width={showExternalStatusColumn ? "10%" : "15%"}>
                                            Created
                                        </Table.TH>
                                        <Table.TH sorting={{}} width="60px">
                                            {" "}
                                        </Table.TH>
                                    </Table.TR>
                                </Table.THead>
                                <Table.TBody>{userList?.map((user) => displayRecord(user))}</Table.TBody>
                            </Table>
                        </div>
                    </>
                )}
            </div>
        </LimitByRole>
    );
};

UserTable.displayName = "UserTable";

export default UserTable;
